import { useState, useRef, useEffect } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { Grid, Box, TextField, Typography, Alert, AlertTitle, Paper, IconButton, Switch, FormControlLabel, LinearProgress, CircularProgress } from '@mui/material'
import CancelIcon from '@mui/icons-material/Cancel'
import { Page, PageTitle, ActionBar, Sheet } from '../../components'
import Button from '@mui/material/Button'
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner'
import BarcodeScanner, { isCameraAvailable } from '../../components/BarcodeScanner'
import useFetch from '../../hooks/useFetch'
import getSlotArt, { getSlotNote } from './slotArt'
import { isUnreadablePhoto } from '../../photoFiles'
import { getRejected } from './rejectedApi'
import RejectedShots from './RejectedShots'
import ProductFields, { fieldLabel } from './ProductFields'
import useProductFields from './useProductFields'
import { reshootPiece } from '../photo-review/publishApi'

// Categories that may be bangles — there is no bangle product category, so the
// UI offers a toggle and uploads under the 'Bangle Bracelets' template alias.
const BANGLE_CANDIDATE_CATEGORIES = ['Bracelets', "Men's Bracelets", 'Baby Bracelets'];
const BANGLE_ALIAS = 'Bangle Bracelets';

// slot key -> display name ('standing-front' -> 'Standing Front', 'angle3' -> 'Angle 3')
// Kept as the fallback for any slot key that has no photoUpload.slots.* entry.
export function formatSlotName(key) {
    return key
        .replace(/-/g, ' ')
        .replace(/(\d+)/, ' $1')
        .trim()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

// slot key -> camelCase catalog suffix ('standing-front' -> 'standingFront')
function slotKeyToCamel(key) {
    return key.replace(/-([a-z0-9])/g, (_, c) => c.toUpperCase());
}

// Module-level helper (not a component/hook), so `t` is passed in — same
// treatment as InventoryReport's `scopeText`. Falls back to formatSlotName
// for any slot key with no explicit catalog entry.
export function slotDisplayName(t, key) {
    const translated = t(`photoUpload.slots.${slotKeyToCamel(key)}`, { defaultValue: '' });
    return translated || formatSlotName(key);
}

export default function PhotoUpload(props) {
    const { t } = useTranslation();
    const { setAuth } = props;
    const { fetchResource, isPending } = useFetch({ setAuth });
    const inputRef = useRef();
    const bufferRef = useRef('');
    const timerRef = useRef(null);
    const itemActiveRef = useRef(false);
    const [sku, setSku] = useState('');
    const [showScanner, setShowScanner] = useState(false);
    const [item, setItem] = useState(null);
    const [scanMessage, setScanMessage] = useState(null);
    const [assignments, setAssignments] = useState({});
    const [isBangle, setIsBangle] = useState(false);
    const [bangleTemplate, setBangleTemplate] = useState(null);
    const [unreadablePick, setUnreadablePick] = useState(false);
    const [rejectedGroups, setRejectedGroups] = useState([]);
    const productFields = useProductFields(fetchResource);
    // A published piece scanned: "Do you want to reshoot it?" (the item), the
    // reshoot request running, and the server's refusal.
    const [reshootAsk, setReshootAsk] = useState(null);
    const [reshootBusy, setReshootBusy] = useState(false);
    const [reshootError, setReshootError] = useState(null);
    // The hardware scanner stays out of the way while an item or the
    // reshoot question is open.
    itemActiveRef.current = Boolean(item) || Boolean(reshootAsk);
    // A reopened piece (a reshoot, or Reopen piece on the review page): new
    // photos replace the live ones shot by shot, and an empty slot keeps its
    // live photo (the lookup lists every live slot in `onFile`).
    const reshooting = Boolean(item?.republish);

    // The effective category/template drive slots, art, and the upload payload;
    // they diverge from the item's own only when the Bangle toggle is on.
    const effectiveCategory = isBangle ? BANGLE_ALIAS : item?.category;
    const effectiveTemplate = isBangle && bangleTemplate ? bangleTemplate : item?.template;

    // The open item's own rejected shots, by slot key -- comes from the same
    // list RejectedShots renders, matched by sku. No separate endpoint for it.
    const rejectedGroupForItem = item ? rejectedGroups.find(group => group.sku === item.sku) : null;
    const rejectedBySlot = rejectedGroupForItem
        ? Object.fromEntries(rejectedGroupForItem.shots.map(shot => [shot.slot, shot]))
        : {};

    // Slots already on file for this item and not rejected (the items
    // lookup's `onFile`). A required one counts as done, so a reshoot sends
    // only the rejected shot: re-sending the good ones would reset them on
    // the server, wiping their edits and approvals. Still replaceable.
    const onFileSlots = new Set(item?.onFile ?? []);
    // Slots a reviewer removed from the piece (the lookup's `waived`): it goes
    // ahead without them, so they are not required. Uploading one again
    // brings it back.
    const waivedSlots = new Set(item?.waived ?? []);
    const requiredSlots = effectiveTemplate ? effectiveTemplate.slots.filter(slot => slot.required && !waivedSlots.has(slot.key)) : [];
    const isSatisfied = slot => Boolean(assignments[slot.key]) || onFileSlots.has(slot.key);
    // A required product field still empty (or a value the server would
    // refuse) holds the upload back too; unsaved edits are saved on Upload.
    const canUpload = Object.keys(assignments).length > 0 && requiredSlots.every(isSatisfied)
        && !productFields.blocking && !productFields.saving;

    async function fetchRejected() {
        const response = await getRejected(fetchResource);
        // A failed fetch resolves to {error} (useFetch's catch shape), not an
        // array. Leave the list as it was rather than wiping a real backlog
        // over a transient failure -- useFetch's own alert already surfaces
        // the error, but that banner auto-dismisses after 5s.
        if (Array.isArray(response)) setRejectedGroups(response);
    }

    function clearAssignments() {
        setAssignments(prev => {
            Object.values(prev).forEach(assigned => URL.revokeObjectURL(assigned.previewUrl));
            return {};
        });
    }

    function handleCancelItem() {
        clearAssignments();
        setUnreadablePick(false);
        setIsBangle(false);
        setItem(null);
        productFields.reset();
    }

    function handleBangleToggle(e) {
        setIsBangle(e.target.checked);
        clearAssignments();
    }

    function handleSlotFile(slotKey, e) {
        const file = e.target.files?.[0];
        if (!file) return;
        e.target.value = '';
        if (isUnreadablePhoto(file)) {
            setUnreadablePick(true);
            return;
        }
        setUnreadablePick(false);
        setAssignments(prev => {
            if (prev[slotKey]) URL.revokeObjectURL(prev[slotKey].previewUrl);
            return { ...prev, [slotKey]: { file, previewUrl: URL.createObjectURL(file) } };
        });
    }

    async function handleUpload() {
        if (isPending('upload')) return;
        // The clerk checked the fields before shooting; what they changed goes
        // up first. A refused save keeps the item open with its photos.
        if (!(await productFields.save())) return;
        const formData = new FormData();
        const itemAssignments = [];
        effectiveTemplate.slots.forEach(slot => {
            const assigned = assignments[slot.key];
            if (!assigned) return;
            itemAssignments.push({ slot: slot.key, fileIndex: itemAssignments.length });
            formData.append('photos', assigned.file);
        });
        formData.append('items', JSON.stringify([
            { sku: item.sku, category: effectiveCategory, assignments: itemAssignments },
        ]));

        const response = await fetchResource({ resource: 'photo-pipeline/upload', body: formData, loadingKey: 'upload' });
        // On failure keep the item view intact — uploads are idempotent upserts,
        // so the user just retries the same batch.
        if (response.error) return;

        Object.values(assignments).forEach(assigned => URL.revokeObjectURL(assigned.previewUrl));
        setScanMessage({ type: 'success', text: t('photoUpload.uploadSuccess', { sku: item.sku, count: response.uploaded }) });
        setAssignments({});
        setItem(null);
        productFields.reset();
        fetchRejected();
    }

    function handleRemoveSlot(slotKey, e) {
        e.preventDefault();
        setAssignments(prev => {
            if (prev[slotKey]) URL.revokeObjectURL(prev[slotKey].previewUrl);
            const { [slotKey]: removed, ...rest } = prev;
            return rest;
        });
    }

    function handleSkuInput(e) {
        const { value } = e.target;
        if (!/^\d*$/.test(value)) return;
        if (value.length > 8) return;
        setSku(value);
    }

    function handleKeyDown(e) {
        if (e.key === 'Enter' && sku.length >= 6 && sku.length <= 8) handleSubmitSku(sku);
    }

    async function handleSubmitSku(skuToFetch) {
        try {
            const response = await fetchResource({
                method: 'GET',
                resource: `photo-pipeline/items?skus=${skuToFetch}`,
                loadingKey: 'sku',
            });
            if (!response.items?.length) throw new Error(t('photoUpload.fetchError'));
            setSku('');
            const scannedItem = response.items[0];
            if (!scannedItem.found) {
                setScanMessage({
                    type: 'warning',
                    text: t('photoUpload.itemNotFound', { sku: skuToFetch }),
                });
                return;
            }
            // On the site and not reopened: the upload route would refuse it,
            // so ask first. Yes reopens it for a reshoot (handleReshoot).
            if (scannedItem.published) {
                setScanMessage(null);
                setReshootError(null);
                setReshootAsk(scannedItem);
                return;
            }
            if (!scannedItem.template) {
                setScanMessage({
                    type: 'warning',
                    text: t('photoUpload.noTemplate', { category: scannedItem.category }),
                });
                return;
            }
            setScanMessage(null);
            setIsBangle(false);
            setItem(scannedItem);
            productFields.load(scannedItem.sku);
            if (BANGLE_CANDIDATE_CATEGORIES.includes(scannedItem.category) && !bangleTemplate) {
                const templatesResponse = await fetchResource({
                    method: 'GET',
                    resource: 'photo-pipeline/templates',
                });
                if (templatesResponse.templates?.[BANGLE_ALIAS]) setBangleTemplate(templatesResponse.templates[BANGLE_ALIAS]);
            }
        } catch (error) {
            console.error(error);
        }
    }

    // Yes: the piece is reopened in reshoot mode, then looked up again, which
    // now opens the normal upload view (it is no longer `published`).
    async function handleReshoot() {
        if (reshootBusy || !reshootAsk) return;
        setReshootBusy(true);
        setReshootError(null);
        const response = await reshootPiece(fetchResource, reshootAsk.sku);
        setReshootBusy(false);
        if (response?.error) {
            setReshootError(response.error);
            return;
        }
        const sku = reshootAsk.sku;
        setReshootAsk(null);
        await handleSubmitSku(sku);
    }

    useEffect(() => {
        if (!item && !reshootAsk) inputRef.current?.focus();
    }, [item, reshootAsk]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchRejected(); }, []);

    // ?sku= (the piece preview's "Corrigir dados") opens that piece as if it
    // were scanned. Read once, then dropped from the address, so a reload or
    // Cancel comes back to the scan screen.
    useEffect(() => {
        const linked = new URLSearchParams(window.location.search).get('sku');
        if (!linked) return;
        window.history.replaceState(window.history.state, '', window.location.pathname);
        // The server's SKU rule (photoPipeline/productFields.js): 7 or 8 digits.
        if (/^\d{7,8}$/.test(linked)) handleSubmitSku(linked);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Hardware scanner support: collect digits typed while the input is not
    // focused (same pattern as InventoryCheck).
    useEffect(() => {
        const handleWindowKeyDown = e => {
            if (itemActiveRef.current) return;
            const inputIsFocused = inputRef.current && document.activeElement === inputRef.current;
            if (inputIsFocused) return;

            if (!/^[0-9]$/.test(e.key)) {
                if (e.key === 'Enter') {
                    if (bufferRef.current.length >= 6 && bufferRef.current.length <= 8) handleSubmitSku(bufferRef.current);
                    bufferRef.current = '';
                }
                return;
            }

            bufferRef.current += e.key;

            clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => {
                bufferRef.current = '';
            }, 100);
        };

        window.addEventListener('keydown', handleWindowKeyDown);
        return () => window.removeEventListener('keydown', handleWindowKeyDown);
    }, []);

    // Grows to fill the page and never shrinks below its content. The page
    // body is a fixed-height flex column that scrolls; with `height='100%'`
    // a reshoot list taller than the screen could not grow the column, so its
    // centred content overflowed -- the controls wrapped into a second column
    // off the right edge of a phone. `minHeight='100%'` alone does not help:
    // as a flex item the column still shrinks to fit. `flex: 1 0 auto` keeps a
    // short column centred in the page and lets a tall one scroll normally.
    const Scan = () => (
        <Grid
            container
            direction='column'
            justifyContent='center'
            alignItems='center'
            sx={{ flex: '1 0 auto' }}
            gap={2}
        >
            <Grid item sx={{ width: '100%', maxWidth: '380px' }}>
                <RejectedShots groups={rejectedGroups} onOpen={handleSubmitSku} slotLabel={key => slotDisplayName(t, key)} />
            </Grid>
            {scanMessage && (
                <Grid item>
                    {/* The piece's own photo stands in for the icon when there is one,
                        so the clerk sees which piece was refused. */}
                    <Alert
                        severity={scanMessage.type}
                        icon={scanMessage.imageUrl ? (
                            <img src={scanMessage.imageUrl} alt={t('photoUpload.productAlt')} style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 6 }} />
                        ) : undefined}
                        sx={{ maxWidth: '380px', alignItems: 'center' }}
                    >
                        {scanMessage.text}
                    </Alert>
                </Grid>
            )}
            <Grid item>
                <Typography variant='h6'>{t('photoUpload.scanItem')}</Typography>
            </Grid>
            <Grid item>
                <TextField
                    label={t('photoUpload.skuLabel')}
                    value={sku}
                    onChange={handleSkuInput}
                    onKeyDown={handleKeyDown}
                    inputRef={inputRef}
                    focused
                />
            </Grid>
            {/* The third way in, beside a typed SKU and the hardware scanner. What
                stood here before was a 48px QrCodeScannerIcon: it looked exactly
                like the control and did nothing at all. */}
            {isCameraAvailable() && (
                <Grid item>
                    <Button
                        variant='outlined'
                        color='inherit'
                        startIcon={<QrCodeScannerIcon />}
                        onClick={() => setShowScanner(true)}
                    >
                        {t('common.scan')}
                    </Button>
                </Grid>
            )}
            {/* Fires on every SKU submit -- stays small, non-blocking, and reserves its
                own space rather than shifting the layout on toggle (InventoryCheck's
                scan indicator pattern). */}
            <Grid item height='24px'>
                {isPending('sku') && <CircularProgress size={20} data-testid='sku-busy' />}
            </Grid>
        </Grid>
    )

    const Item = () => (
        <Grid
            container
            direction='column'
            justifyContent='center'
            alignItems='center'
            gap={2}
        >
            {/* A reshoot starts from empty slots: the live photo is what is being replaced. */}
            {item.imageUrl && !reshooting && (
                <Grid item>
                    <img src={item.imageUrl} style={{ height: '200px' }} alt={t('photoUpload.productAlt')} />
                </Grid>
            )}
            {reshooting && (
                <Grid item sx={{ width: '100%', maxWidth: '536px', padding: '0 8px' }}>
                    <Alert severity='warning'>
                        <AlertTitle sx={{ fontWeight: 600 }}>{t('photoUpload.reshoot.bannerTitle')}</AlertTitle>
                        {t('photoUpload.reshoot.banner')}
                    </Alert>
                </Grid>
            )}
            <Grid item container direction='row' justifyContent='space-evenly' sx={{ padding: '5px', gap: '1.4rem' }}>
                <Typography variant='h6'>
                    <Trans i18nKey='photoUpload.categoryLine' values={{ category: item.category }} components={{ b: <b /> }} />
                </Typography>
                <Typography variant='h6'>
                    <Trans i18nKey='photoUpload.skuLine' values={{ sku: item.sku }} components={{ b: <b /> }} />
                </Typography>
            </Grid>
            {BANGLE_CANDIDATE_CATEGORIES.includes(item.category) && bangleTemplate && (
                <Grid item>
                    <FormControlLabel
                        control={<Switch checked={isBangle} onChange={handleBangleToggle} />}
                        label={t('photoUpload.bangleToggle')}
                    />
                </Grid>
            )}
            <Grid item sx={{ width: '100%', maxWidth: '536px', padding: '0 8px' }}>
                <ProductFields form={productFields} onRetry={() => productFields.load(item.sku)} />
            </Grid>
            {unreadablePick && (
                <Grid item sx={{ padding: '0 8px' }}>
                    <Alert severity='error'>
                        <Trans i18nKey='photoUpload.unreadablePhoto' components={{ b: <b /> }} />
                    </Alert>
                </Grid>
            )}
            <Grid item>
                <Typography color='text.secondary'>
                    {t('photoUpload.requiredPhotosCount', {
                        assigned: requiredSlots.filter(isSatisfied).length,
                        total: requiredSlots.length,
                    })}
                </Typography>
            </Grid>
            <Grid item container direction='row' justifyContent='center' gap={1.5} sx={{ padding: '0 8px' }}>
                {effectiveTemplate.slots.map(slot => {
                    const assigned = assignments[slot.key];
                    const rejectedShot = rejectedBySlot[slot.key];
                    return (
                        <Grid item key={slot.key} sx={{ width: '104px' }}>
                        <Paper
                            component='label'
                            variant='outlined'
                            sx={{
                                width: '104px',
                                height: '104px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderStyle: assigned ? 'solid' : 'dashed',
                                cursor: 'pointer',
                                overflow: 'hidden',
                                position: 'relative',
                                ...(rejectedShot && { borderColor: 'error.main', borderStyle: 'solid', borderWidth: '2px' }),
                            }}
                        >
                            <input
                                type='file'
                                accept='image/jpeg,image/png'
                                hidden
                                aria-label={slotDisplayName(t, slot.key)}
                                onChange={e => handleSlotFile(slot.key, e)}
                            />
                            {assigned ? (<>
                                <img
                                    src={assigned.previewUrl}
                                    alt={t('photoUpload.slotPhotoAlt', { slot: slotDisplayName(t, slot.key) })}
                                    style={{ position: 'absolute', width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                                <IconButton
                                    aria-label={t('photoUpload.removeSlotPhoto', { slot: slotDisplayName(t, slot.key) })}
                                    size='small'
                                    onClick={e => handleRemoveSlot(slot.key, e)}
                                    sx={{
                                        position: 'absolute',
                                        top: 2,
                                        right: 2,
                                        color: '#fff',
                                        backgroundColor: 'rgba(0,0,0,0.45)',
                                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.65)' },
                                    }}
                                >
                                    <CancelIcon fontSize='small' />
                                </IconButton>
                            </>) : (<>
                                {getSlotArt(effectiveCategory, slot.key) && (
                                    <img
                                        src={getSlotArt(effectiveCategory, slot.key)}
                                        alt={t('photoUpload.slotExampleAlt', { slot: slotDisplayName(t, slot.key) })}
                                        style={{
                                            width: '52px',
                                            height: '52px',
                                            objectFit: 'contain',
                                            filter: 'grayscale(1)',
                                            opacity: 0.4,
                                        }}
                                    />
                                )}
                                <Typography variant='body2' fontWeight='bold'>{slotDisplayName(t, slot.key)}</Typography>
                                {onFileSlots.has(slot.key) ? (
                                    <Typography variant='caption' color='success.main'>
                                        {t(reshooting ? 'photoUpload.reshoot.keepsLive' : 'photoUpload.onFile')}
                                    </Typography>
                                ) : waivedSlots.has(slot.key) ? (
                                    <Typography variant='caption' color='text.secondary'>
                                        {t('photoUpload.waived')}
                                    </Typography>
                                ) : (
                                    <Typography variant='caption' color={slot.required ? 'error' : 'text.secondary'}>
                                        {slot.required ? t('photoUpload.required') : t('photoUpload.optional')}
                                    </Typography>
                                )}
                            </>)}
                        </Paper>
                        {getSlotNote(t, effectiveCategory, slot.key) && (
                            <Typography
                                variant='caption'
                                color='text.secondary'
                                sx={{ display: 'block', fontSize: '0.62rem', lineHeight: 1.25, mt: '3px', textAlign: 'center' }}
                            >
                                {getSlotNote(t, effectiveCategory, slot.key)}
                            </Typography>
                        )}
                        {rejectedShot && (
                            <Typography
                                variant='caption'
                                color='error'
                                sx={{ display: 'block', fontSize: '0.62rem', lineHeight: 1.25, mt: '3px', textAlign: 'center' }}
                            >
                                {t('photoUpload.rejected.slotMarker', { reason: t(`photoReview.reject.reasons.${rejectedShot.reason}`) })}
                            </Typography>
                        )}
                        </Grid>
                    );
                })}
            </Grid>
            {effectiveTemplate.slots.some(slot => !slot.required) && (
                <Grid item sx={{ maxWidth: '380px' }}>
                    <Typography variant='caption' color='text.secondary' align='center' sx={{ display: 'block' }}>
                        {t('photoUpload.optionalViewHint')}
                    </Typography>
                </Grid>
            )}
            {productFields.blocking && (
                <Grid item>
                    <Typography variant='caption' color='error'>
                        {t('photoUpload.fields.fillRequired', { fields: Object.keys(productFields.problems).map(key => fieldLabel(t, key)).join(', ') })}
                    </Typography>
                </Grid>
            )}
            <Grid item sx={{ width: '100%', maxWidth: '380px' }}>
                <ActionBar
                    cancel={{ label: t('common.cancel'), onClick: handleCancelItem, disabled: isPending('upload') }}
                    confirm={{
                        label: t('photoUpload.uploadItem'),
                        onClick: handleUpload,
                        disabled: isPending('upload') || !canUpload
                    }}
                />
            </Grid>
            {isPending('upload') && (
                <Grid item sx={{ width: '220px' }}>
                    <LinearProgress />
                    <Typography variant='caption' color='text.secondary'>{t('photoUpload.uploadingPhotos')}</Typography>
                </Grid>
            )}
        </Grid>
    )

    return (
        <Page alignItems='center'>
            <PageTitle>{t('photoUpload.title')}</PageTitle>
            {item ? Item() : Scan()}

            {/* A sibling of the two views rather than a child of Scan: Scan is
                declared inside this render body, so it is a new component type on
                every render and anything inside it is torn down and rebuilt --
                which for a camera means re-acquiring the stream. */}
            <Sheet
                open={Boolean(reshootAsk)}
                onClose={() => { if (!reshootBusy) setReshootAsk(null); }}
                title={t('photoUpload.reshoot.title')}
                actions={(
                    <ActionBar
                        cancel={{ label: t('photoUpload.reshoot.no'), onClick: () => setReshootAsk(null), disabled: reshootBusy }}
                        confirm={{
                            label: t('photoUpload.reshoot.yes'),
                            onClick: handleReshoot,
                            disabled: reshootBusy,
                            startIcon: reshootBusy ? <CircularProgress size={16} color='inherit' /> : null,
                        }}
                    />
                )}
            >
                {reshootAsk && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            {reshootAsk.imageUrl && (
                                <img src={reshootAsk.imageUrl} alt={t('photoUpload.productAlt')} style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6 }} />
                            )}
                            <Box>
                                <Typography sx={{ fontWeight: 600 }}>{reshootAsk.sku}</Typography>
                                {reshootAsk.category && <Typography variant='body2' color='text.secondary'>{reshootAsk.category}</Typography>}
                            </Box>
                        </Box>
                        <Typography variant='body2'>{t('photoUpload.reshoot.body')}</Typography>
                        {reshootError && <Alert severity='error'>{reshootError}</Alert>}
                    </Box>
                )}
            </Sheet>

            {showScanner && (
                <BarcodeScanner
                    open={showScanner}
                    onClose={() => setShowScanner(false)}
                    onScan={code => {
                        setShowScanner(false);
                        setSku(code);
                        handleSubmitSku(code);
                    }}
                    title={t('photoUpload.scanItem')}
                />
            )}
        </Page>
    )
}
